/** Formes renvoyées par /api/compte — un seul endroit pour les tenir à jour. */

export type Lien = { libelle: string; url: string };
export type Offre = { nom: string; detail: string | null };
export type Sport = { nom: string; niveau: string | null; cote: 'gauche' | 'droite' | null };

export type Profil = {
  pseudo: string | null;
  numero_tva: string | null;
  offres: Offre[];
  demandes: Offre[];
  sports: Sport[];
  telephone: string | null;
  entreprise: string | null;
  fonction: string | null;
  secteur: string | null;
  taille_entreprise: string | null;
  ca_annuel: string | null;
  bio: string | null;
  services: string | null;
  recherche: string | null;
  client_ideal: string | null;
  partenaire_ideal: string | null;
  pour_quoi_faire: string | null;
  pitch: string | null;
  pitch_valide_le: string | null;
  pitch_brouillon: string | null;
  pitch_brouillon_le: string | null;
  site_web: string | null;
  linkedin: string | null;
  photo_url: string | null;
  logo_url: string | null;
  interets: string[];
  objectifs: string[];
  forces: string[];
  sujets_conversation: string[];
  situation: string | null;
  origine_geographique: string | null;
  tranche_age: string | null;
  liens: Lien[];
  visible_annuaire: boolean;
  contact_avant_evenement: boolean;
  contact_apres_evenement: boolean;
  visibilite_profil: 'toujours' | 'apres_match';
  speed_meetings: boolean;
  navigation_discrete: boolean;
};

export type Membre = {
  id: string;
  prenom: string;
  nom: string | null;
  fonction: string | null;
  email: string | null;
  telephone: string | null;
  forces: string[];
  liens: Lien[];
  photo_url: string | null;
};

export type Participation = {
  participant_id: string;
  evenement_id: string;
  nom: string;
  slug: string;
  lieu_nom: string | null;
  lieu_ville: string | null;
  debut_le: string;
  fin_le: string;
  aujourdhui: boolean;
};

export type Espace = {
  identite: { prenom: string | null; nom: string | null; email: string | null };
  profil: Profil | null;
  equipe: Membre[];
  participations: Participation[];
  demandes_en_attente: number;
  ia_disponible: boolean;
  a_mot_de_passe: boolean;
  doit_redefinir: boolean;
  admin: boolean;
};

export type Relation = 'aucune' | 'demande_envoyee' | 'demande_recue' | 'match' | 'declinee';

export type Carte = {
  personne_id: string;
  prenom: string | null;
  nom: string | null;
  email: string | null;
  telephone: string | null;
  entreprise: string | null;
  fonction: string | null;
  secteur: string | null;
  ca_annuel: string | null;
  bio: string | null;
  services: string | null;
  recherche: string | null;
  client_ideal: string | null;
  pitch: string | null;
  site_web: string | null;
  linkedin: string | null;
  photo_url: string | null;
  interets: string[];
  objectifs: string[];
  forces: string[];
  sujets_conversation: string[];
  situation: string | null;
  origine_geographique: string | null;
  liens: Lien[];
  visibilite_profil: 'toujours' | 'apres_match';
  speed_meetings: boolean;
  score: number;
  interets_communs: string[];
  objectifs_communs: string[];
  sujets_communs: string[];
  forces_complementaires: { mienne: string; sienne: string }[];
  je_reponds_a_sa_demande: string[];
  il_repond_a_ma_demande: string[];
  explication: string;
  brise_glace: string | null;
  relation: Relation;
  match_id: string | null;
  contact_visible: boolean;
  nouveau: boolean;
  /** Membre Premium : l'étoile, et la première place dans les listes. */
  premium?: boolean;
  evenement_nom?: string | null;
  demande_le?: string;
  match_le?: string;
};

export type Fiche = Carte & { equipe: { prenom: string; nom: string | null; fonction: string | null; forces: string[]; liens: Lien[] }[] };

export type EvenementOuvert = {
  id: string;
  nom: string;
  description: string | null;
  lieu_nom: string | null;
  lieu_ville: string | null;
  debut_le: string;
  organisation_nom: string;
  categorie: 'networking' | 'sport' | 'formation';
  sous_categorie: 'jouer' | 'regarder' | null;
  aujourdhui: boolean;
  participants: number;
};

export type EvenementRecommande = EvenementOuvert & { score: number; raison: string; jours_avant: number; compatibles: number };

export type EvenementAVenir = { evenement_id: string; nom: string; debut_le: string; inscrits_avec_compte: number };

export type EvenementPasse = {
  participant_id: string;
  evenement_id: string;
  nom: string;
  lieu_ville: string | null;
  debut_le: string;
  contacts: number;
  rendez_vous: number;
};

export type Flux = {
  depuis: string | null;
  nouveautes: { vues: number; clics: number; demandes: number; matchs: number; nouveaux_compatibles: number };
  demandes_recues: Carte[];
  matchs: Carte[];
  vues: { total: number; recentes: { par_id: string | null; prenom: string | null; nom: string | null; entreprise: string | null; vu_le: string }[] };
  liens: { total: number; par_lien: { url: string; clics: number; dernier: string }[]; recents: { url: string; prenom: string | null; nom: string | null; entreprise: string | null; clique_le: string }[] };
  nouveaux_compatibles: Carte[];
  classement: { top: Rang[]; moi: Rang | null; total: number };
  evenements_recommandes: EvenementRecommande[];
  evenements_a_venir: EvenementAVenir[];
  evenements_passes: EvenementPasse[];
};

export type Rang = {
  rang: number;
  personne_id: string;
  prenom: string | null;
  nom: string | null;
  entreprise: string | null;
  secteur: string | null;
  vues: number;
  clics: number;
  demandes: number;
  traction: number;
  moi: boolean;
};

export type Partenaire = {
  id: string;
  nom: string;
  accroche: string;
  description: string | null;
  url: string;
  logo_url: string | null;
  categorie: string | null;
};

/* ————————————————— Petits utilitaires d'affichage ————————————————— */

export const nomDe = (p: { prenom: string | null; nom: string | null }) =>
  [p.prenom, p.nom].filter(Boolean).join(' ') || 'Entrepreneur';

export const dateLongue = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });
export const dateCourte = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' });
export const heure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

export const quandRelatif = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 2) return 'à l’instant';
  if (min < 60) return `il y a ${min} min`;
  if (min < 60 * 24) return `il y a ${Math.round(min / 60)} h`;
  const j = Math.round(min / 60 / 24);
  return j === 1 ? 'hier' : `il y a ${j} j`;
};

export const TRANCHES_CA = [
  { valeur: '', texte: 'Non précisé' },
  { valeur: 'moins_100k', texte: 'Moins de 100 k€' },
  { valeur: '100k_500k', texte: '100 k€ – 500 k€' },
  { valeur: '500k_1m', texte: '500 k€ – 1 M€' },
  { valeur: '1m_5m', texte: '1 M€ – 5 M€' },
  { valeur: '5m_20m', texte: '5 M€ – 20 M€' },
  { valeur: 'plus_20m', texte: 'Plus de 20 M€' },
];
export const libelleCa = (v: string | null) => TRANCHES_CA.find((t) => t.valeur === v)?.texte ?? null;

export const TAILLES = [
  { valeur: '', texte: 'Non précisée' },
  { valeur: '1', texte: 'Indépendant' },
  { valeur: '2-9', texte: '2 à 9 personnes' },
  { valeur: '10-49', texte: '10 à 49' },
  { valeur: '50-249', texte: '50 à 249' },
  { valeur: '250+', texte: '250 et plus' },
];

export const TRANCHES_AGE = [
  { valeur: '', texte: 'Non précisée' },
  { valeur: '18-25', texte: '18 – 25 ans' },
  { valeur: '26-35', texte: '26 – 35 ans' },
  { valeur: '36-45', texte: '36 – 45 ans' },
  { valeur: '46-55', texte: '46 – 55 ans' },
  { valeur: '56+', texte: '56 ans et plus' },
];

export const FORCES = ['Tech', 'Commercial', 'Marketing', 'Produit', 'Finance', 'Opérations', 'Créatif', 'Juridique', 'RH', 'Management'];

export const OBJECTIFS = [
  'Croissance', 'Nouveaux clients', 'Partenariats', 'Recruter', 'Lever des fonds', 'Export', 'Fournisseurs',
  'Innovation / R&D', 'Transmission', 'Franchise', 'Reprise / cession', 'Investir', 'Sous-traitance',
  'Visibilité locale', 'Diversification', 'Digitalisation',
];

/** Les sports les plus fréquents, pour le sélecteur ; la recherche accepte n'importe quel autre. */
export const SPORTS_FREQUENTS = [
  'Tennis', 'Padel', 'Golf', 'Running', 'Vélo', 'Football', 'Basket', 'Natation', 'Ski', 'Voile', 'Randonnée',
  'Fitness', 'Yoga', 'Trail', 'Triathlon', 'Badminton', 'Squash', 'Escalade', 'Équitation', 'Boxe', 'Judo',
  'Karaté', 'Rugby', 'Hockey', 'Volley', 'Handball', 'Ping-pong', 'Danse', 'Marathon', 'Kitesurf', 'Surf',
  'Plongée', 'Moto', 'Pêche', 'Pétanque', 'Billard', 'Échecs', 'E-sport', 'CrossFit', 'Marche nordique',
];
/** Sports de raquette qui se jouent en double : on précise aussi le côté. */
export const SPORTS_A_COTE = ['Tennis', 'Padel', 'Badminton', 'Squash', 'Beach-volley'];

/** Les réseaux affichés en dur, avec leur icône, avant « autre réseau ». */
export const RESEAUX_FIXES: { nom: string; placeholder: string; domaines: string[] }[] = [
  { nom: 'LinkedIn', placeholder: 'https://www.linkedin.com/in/…', domaines: ['linkedin'] },
  { nom: 'Facebook', placeholder: 'https://www.facebook.com/…', domaines: ['facebook', 'fb.com'] },
  { nom: 'Instagram', placeholder: 'https://www.instagram.com/…', domaines: ['instagram'] },
  { nom: 'X', placeholder: 'https://x.com/…', domaines: ['x.com', 'twitter'] },
  { nom: 'TikTok', placeholder: 'https://www.tiktok.com/@…', domaines: ['tiktok'] },
  { nom: 'YouTube', placeholder: 'https://www.youtube.com/@…', domaines: ['youtube', 'youtu.be'] },
];

export const SUJETS = ['Sport', 'Running', 'Vélo', 'Voile', 'Golf', 'Cuisine', 'Vin', 'Voyages', 'Musique', 'Lecture', 'Photographie', 'Jardinage', 'Famille', 'Jeux', 'Bénévolat'];

/** Le réseau derrière une URL — pour l'icône et l'intitulé par défaut. */
export function reseauDe(url: string): string {
  try {
    const h = new URL(url).hostname.replace(/^www\./, '');
    if (h.includes('linkedin')) return 'LinkedIn';
    if (h.includes('facebook') || h === 'fb.com') return 'Facebook';
    if (h.includes('instagram')) return 'Instagram';
    if (h === 'x.com' || h.includes('twitter')) return 'X';
    if (h.includes('tiktok')) return 'TikTok';
    if (h.includes('youtube') || h === 'youtu.be') return 'YouTube';
    return h;
  } catch {
    return 'Lien';
  }
}

/** URL traçante vers un lien du profil de quelqu'un d'autre. */
export const lienSuivi = (proprietaireId: string, url: string) =>
  `/api/compte/liens/aller?proprietaire=${proprietaireId}&vers=${encodeURIComponent(url)}`;

/* ————————————————— Pré-enregistrement ————————————————— */

export type ModeAbonnement = 'auto' | 'interesse' | 'libre';

export type Abonnement = {
  id: string;
  statut: 'en_attente' | 'actif' | 'impaye' | 'annule';
  formule: 'preenregistrement' | 'pro' | 'premium';
  closer_id: string | null;
  mode: ModeAbonnement;
  secteurs_cibles: string[];
  debut_le: string | null;
  fin_periode_le: string | null;
  annule_le: string | null;
};

export type EtatAbonnement = {
  abonnement: Abonnement | null;
  actif: boolean;
  prix_centimes: number;
  url_calendrier: string | null;
  preinscriptions: {
    evenement_id: string;
    statut: 'inscrit' | 'tres_interesse' | 'decline';
    origine: 'auto' | 'manuelle';
    raison: string | null;
    nom: string;
    debut_le: string;
    lieu_nom: string | null;
    lieu_ville: string | null;
  }[];
  formule: 'preenregistrement' | 'pro' | 'premium' | null;
  formules: DescriptionFormule[];
};

export type EvenementAvantPremiere = {
  id: string;
  nom: string;
  description: string | null;
  lieu_nom: string | null;
  lieu_ville: string | null;
  debut_le: string;
  organisation_nom: string;
  participants: number;
  ciblage: { concerne: boolean; raison: string; secteurs_presents: string[] };
  public_dans_heures: number;
  deja_public: boolean;
  ma_decision: 'inscrit' | 'tres_interesse' | 'decline' | null;
};

export const eur = (centimes: number) =>
  (centimes / 100).toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' });

/* ————————————————— Influence ————————————————— */

export type RoleEngagement = 'benevole' | 'organisateur' | 'dirigeant' | 'fondateur';
export type PorteeEngagement = 'locale' | 'regionale' | 'nationale' | 'internationale';

export type Engagement = { nom: string; role?: RoleEngagement | null; portee?: PorteeEngagement | null };
export type AudienceSociale = { reseau: string; url?: string | null; abonnes?: number | null };

export type Composante = {
  libelle: string;
  valeur: number;
  poids: number;
  source: 'plateforme' | 'declare';
  detail: string;
};

export type AxeInfluence = { score: number; composantes: Composante[] };

export type MonInfluence = {
  sociale: AxeInfluence;
  reseau: AxeInfluence;
  virtuelle: AxeInfluence;
  global: number;
  palier: string;
  leviers: { quoi: string; gain: number; cible: string }[];
  declarations: {
    caAnnuel: string | null;
    nbEmployes: number | null;
    nbClientsAnnuel: number | null;
    nbClientsBase: number | null;
    engagementDeclare: boolean | null;
    engagements: Engagement[];
    audiences: AudienceSociale[];
  };
  mesures: {
    parrainages: number;
    rendezVous: number;
    matchs: number;
    misesEnRelationReussies: number;
    clicsRecus: number;
    vuesProfil: number;
  };
  progression: { depuis_une_semaine: number | null; historique: { jour: string; global: number }[] };
  premium: boolean;
  boost_premium: number;
  rang: {
    rang: number;
    total: number;
    secteur: string | null;
    dans_mon_secteur: { rang: number; total: number } | null;
  };
};

export type RangInfluence = {
  rang: number;
  personne_id: string;
  prenom: string | null;
  nom: string | null;
  entreprise: string | null;
  secteur: string | null;
  photo_url: string | null;
  sociale: number;
  reseau: number;
  virtuelle: number;
  global: number;
  valeur: number;
  moi: boolean;
};

export type ClassementInfluence = {
  axe: 'global' | 'sociale' | 'reseau' | 'virtuelle';
  secteur: string | null;
  classement: RangInfluence[];
};

export const ROLES_ENGAGEMENT: { valeur: RoleEngagement; texte: string }[] = [
  { valeur: 'benevole', texte: 'J’y donne du temps' },
  { valeur: 'organisateur', texte: 'J’y organise des choses' },
  { valeur: 'dirigeant', texte: 'Je la dirige' },
  { valeur: 'fondateur', texte: 'Je l’ai créée' },
];

export const PORTEES_ENGAGEMENT: { valeur: PorteeEngagement; texte: string }[] = [
  { valeur: 'locale', texte: 'Locale' },
  { valeur: 'regionale', texte: 'Régionale' },
  { valeur: 'nationale', texte: 'Nationale' },
  { valeur: 'internationale', texte: 'Internationale' },
];

export const RESEAUX_SOCIAUX = ['LinkedIn', 'Facebook', 'Instagram', 'YouTube', 'TikTok', 'X', 'Twitch', 'Newsletter'];

/* ————————————————— Parrainage et gains ————————————————— */

export type RoleReseau = 'closer' | 'parrain' | 'parrain_n1' | 'closer_n1';

export const LIBELLE_ROLE: Record<RoleReseau, string> = {
  parrain: 'Parrainage (5 %)',
  parrain_n1: 'Parrainage N-1 (2 %)',
  closer: 'Closer (1 %)',
  closer_n1: 'Ancien barème',
};

/** Le barème du réseau, tel qu'on l'explique — une seule source côté écran. */
export const BAREME_RESEAU_TEXTE = [
  {
    role: 'Parrainage',
    taux: '5 %',
    quoi: 'Toute personne qui s’inscrit via votre lien, votre QR ou votre événement : vous en êtes le parrain, et vous touchez 5 % de toutes ses dépenses, tant qu’elle paie.',
    exemple: '100 filleuls en Premium à 89 €/mois → 445 €/mois.',
  },
  {
    role: 'Parrainage N-1',
    taux: '2 %',
    quoi: 'Votre filleul parraine quelqu’un ? Vous touchez 2 % sur toutes les dépenses des filleuls de vos filleuls.',
    exemple: '100 filleuls de filleuls en Premium → 178 €/mois.',
  },
  {
    role: 'Closer',
    taux: '1 %',
    quoi: 'Vous connectez deux entrepreneurs avec une proposition chiffrée ; celui qui s’abonne pour débloquer la mise en relation vous rapporte 1 % de son abonnement, chaque mois.',
    exemple: '100 personnes passées Premium grâce à vous → 89 €/mois.',
  },
];

export type MonParrainage = {
  code: string;
  url: string;
  qr: string | null;
  /** La page personnelle et son QR — pour la signature et la story. */
  page: string | null;
  qr_page: string | null;
  filleuls: {
    filleul_id: string;
    prenom: string | null;
    nom: string | null;
    entreprise: string | null;
    origine: string;
    cree_le: string;
    formule: string | null;
    abonne: boolean;
  }[];
  gains: {
    du_centimes: number;
    verse_centimes: number;
    total_lignes: number;
    par_role: { role: RoleReseau; montant_centimes: number; lignes: number }[];
    lignes: {
      id: string;
      role: RoleReseau;
      periode: string;
      montant_centimes: number;
      taux: number;
      statut: string;
      prenom: string | null;
      nom: string | null;
      entreprise: string | null;
    }[];
  };
};

/* ————————————————— Mise en relation ————————————————— */

export type Formule = 'preenregistrement' | 'pro' | 'premium';
export type StatutPiste = 'brouillon' | 'proposee' | 'debloquee' | 'rendez_vous' | 'evaluee' | 'refusee' | 'expiree';
export type Evaluation = 'no_match' | 'piste_faible' | 'bonne_piste' | 'perfect_match';

export type ProfilDevoile = {
  personne_id: string;
  prenom: string | null;
  nom: string | null;
  email: string | null;
  telephone: string | null;
  entreprise: string | null;
  fonction: string | null;
  secteur: string | null;
  taille_entreprise: string | null;
  bio: string | null;
  services: string | null;
  pitch: string | null;
  site_web: string | null;
  linkedin: string | null;
  photo_url: string | null;
  logo_url: string | null;
};

export type Reputation = { note: number | null; reussies: number; total: number; proposees?: number };

export type Piste = {
  id: string;
  statut: StatutPiste;
  service: string;
  gain_estime_centimes: number;
  explication: string;
  formule_requise: 'pro' | 'premium';
  proposee_le: string | null;
  note_piste: number | null;
  evaluation: Evaluation | null;
  commentaire: string | null;
  rendez_vous_le: string | null;
  creneaux_proposes: string[];
  vendeur_secteur: string | null;
  vendeur_taille: string | null;
  closer: {
    personne_id: string;
    prenom: string | null;
    nom: string | null;
    entreprise: string | null;
    photo_url: string | null;
    reputation: Reputation;
  } | null;
  vendeur: ProfilDevoile | null;
  acheteur?: ProfilDevoile | null;
  deverrouillable?: boolean;
  deal_declare_centimes?: number | null;
};

export type TableauCloser = {
  proposees: number;
  debloquees: number;
  rendez_vous: number;
  evaluees: number;
  reputation: Reputation;
  ia_disponible: boolean;
  propositions: Piste[];
  evaluations_possibles: { valeur: Evaluation; libelle: string; texte: string }[];
};

export type QuestionVendeur = { cle: string; texte: string; aide: string };

export type DescriptionFormule = {
  cle: Formule;
  nom: string;
  niveau: 1 | 2 | 3;
  prixCentimes: number;
  plafondLeadCentimes: number | null;
  promesse: string;
  avantages: string[];
};

export const euros = (centimes: number) =>
  (centimes / 100).toLocaleString('fr-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

/* ————————————————— Événements : catégories, création, formule Organisateur ————————————————— */

export type CategorieEvenement = 'networking' | 'sport' | 'formation';
export type SousCategorieEvenement = 'jouer' | 'regarder';

export const CATEGORIES_EVENEMENT: { cle: CategorieEvenement; libelle: string; court: string }[] = [
  { cle: 'networking', libelle: 'Networking, Salon & B2B', court: 'Networking' },
  { cle: 'sport', libelle: 'Sport — jouer ou regarder', court: 'Sport' },
  { cle: 'formation', libelle: 'Formation, Conférences, Summit', court: 'Formation' },
];

export const libelleCategorie = (c: string | null | undefined, sous?: string | null) => {
  const base = CATEGORIES_EVENEMENT.find((x) => x.cle === c)?.court ?? 'Événement';
  if (c === 'sport' && sous) return `${base} · ${sous === 'jouer' ? 'on joue' : 'on regarde'}`;
  return base;
};

export type EvenementCree = {
  id: string;
  nom: string;
  slug: string;
  description: string | null;
  categorie: CategorieEvenement;
  sous_categorie: SousCategorieEvenement | null;
  lieu_nom: string | null;
  lieu_ville: string | null;
  adresse: string | null;
  code_postal: string | null;
  image_url: string | null;
  lien_billetterie: string | null;
  site_web: string | null;
  lieu_lat: number | null;
  lieu_lon: number | null;
  debut_le: string;
  fin_le: string;
  statut: string;
  visibilite_publique: boolean;
  participants: number;
};

export type EspaceOrganisateur = {
  organisation: { id: string; nom: string; slug: string; compte_id: string | null } | null;
  abonnement: { statut: string; fin_periode_le: string | null } | null;
  payant: boolean;
  evenements: EvenementCree[];
  evenements_a_venir: number;
  peut_creer_gratuitement: boolean;
  gratuits: number;
  prix_mensuel_centimes: number;
  prix_annuel_centimes: number;
  categories: { cle: CategorieEvenement; libelle: string }[];
};


/* ————————————————— Tableaux d'influence ————————————————— */

export type RangTableau = {
  personne_id: string;
  prenom: string | null;
  nom: string | null;
  entreprise: string | null;
  secteur: string | null;
  photo_url: string | null;
  valeur: number;
  global: number;
  rang: number;
  moi: boolean;
  premium?: boolean;
};
export type Tableau = { top: RangTableau[]; moi: RangTableau | null; total: number };
export type Tableaux = { trending: Tableau; all_time: Tableau };

/* ————————————————— Agenda personnel ————————————————— */

export type Disponibilite = { id?: string; jour_semaine: number; debut_min: number; fin_min: number; duree_min: number };

export type RendezVousPerso = {
  id: string;
  hote_id: string;
  invite_id: string;
  debut_le: string;
  fin_le: string;
  message: string | null;
  statut: 'confirme' | 'annule';
  prenom: string | null;
  nom: string | null;
  entreprise: string | null;
  email: string | null;
  pseudo: string | null;
  je_suis_hote: boolean;
  dans_google: boolean;
};

export type MonAgenda = {
  rendez_vous: RendezVousPerso[];
  quota: { limite: number | null; a_venir: number };
  google: { configure: boolean; connexion: { email: string | null; connecte_le: string; derniere_erreur: string | null } | null };
  disponibilites: Disponibilite[];
};

export const JOURS_SEMAINE = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
export const minutesVersHeure = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
export const heureVersMinutes = (h: string) => {
  const [hh, mm] = h.split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
};

export type PagePublique = {
  profil: {
    personne_id: string;
    prenom: string | null;
    nom: string | null;
    pseudo: string;
    entreprise: string | null;
    fonction: string | null;
    secteur: string | null;
    bio: string | null;
    pitch: string | null;
    services: string | null;
    client_ideal: string | null;
    offres: Offre[];
    site_web: string | null;
    linkedin: string | null;
    photo_url: string | null;
    logo_url: string | null;
    liens: Lien[];
    taille_entreprise: string | null;
    sujets_conversation: string[];
    premium?: boolean;
  };
  influence: { global: number; sociale: number; reseau: number; virtuelle: number; palier: string } | null;
  creneaux: { debut: string; fin: string }[];
  quota: { limite: number | null; a_venir: number; complet: boolean };
  disponibilites_definies: boolean;
  c_est_moi: boolean;
  inscription_url: string;
  code_parrainage: string;
};

/* ————————————————— Événements publics ————————————————— */

export type EvenementPublic = {
  id: string;
  slug: string;
  nom: string;
  description: string | null;
  categorie: CategorieEvenement;
  sous_categorie: SousCategorieEvenement | null;
  lieu_nom: string | null;
  adresse: string | null;
  code_postal: string | null;
  lieu_ville: string | null;
  lieu_pays: string;
  lieu_lat: number | null;
  lieu_lon: number | null;
  image_url: string | null;
  lien_billetterie: string | null;
  site_web: string | null;
  debut_le: string;
  fin_le: string;
  statut: string;
  mise_en_avant: boolean;
  organisation_id: string;
  organisation_nom: string;
  organisation_slug: string;
  organisation_logo: string | null;
  organisation_site: string | null;
  organisation_description: string | null;
  participants: number;
  billets_maison: boolean;
  aujourdhui: boolean;
  passe: boolean;
  distance_km: number | null;
};

export type ZoneVisiteur = { lat: number; lon: number; ville: string; rayon_km: number | null; source: 'cookie' | 'ip' | 'defaut' };

export type ReponseEvenements = {
  zone: ZoneVisiteur;
  rayons: (number | null)[];
  vedette: EvenementPublic | null;
  a_venir: EvenementPublic[];
  hors_zone: number;
  passes: EvenementPublic[];
  categories: { cle: CategorieEvenement; libelle: string }[];
};

export type TypeSession =
  | 'accueil' | 'keynote' | 'conference' | 'table_ronde' | 'speed_meeting' | 'atelier'
  | 'pause' | 'lunch' | 'networking' | 'afterwork' | 'autre';

export const TYPES_SESSION: { cle: TypeSession; libelle: string }[] = [
  { cle: 'accueil', libelle: 'Accueil' },
  { cle: 'keynote', libelle: 'Keynote' },
  { cle: 'conference', libelle: 'Conférence' },
  { cle: 'table_ronde', libelle: 'Table ronde' },
  { cle: 'speed_meeting', libelle: 'Speed meeting' },
  { cle: 'atelier', libelle: 'Atelier' },
  { cle: 'pause', libelle: 'Pause & networking' },
  { cle: 'lunch', libelle: 'Lunch & networking' },
  { cle: 'networking', libelle: 'Networking' },
  { cle: 'afterwork', libelle: 'Afterwork' },
  { cle: 'autre', libelle: 'Autre' },
];
export const libelleType = (t: string) => TYPES_SESSION.find((x) => x.cle === t)?.libelle ?? t;

export type Intervenant = { nom: string; entreprise?: string | null; role?: string | null };

export type SessionProgramme = {
  id: string;
  titre: string;
  description: string | null;
  type: TypeSession;
  debut_le: string;
  fin_le: string;
  salle: string | null;
  entreprise: string | null;
  intervenants: Intervenant[];
  capacite: number | null;
  lien: string | null;
  participants: number;
};

export type FicheEvenement = {
  evenement: EvenementPublic;
  programme: SessionProgramme[];
  entreprises: { personne_id: string; entreprise: string | null; secteur: string | null; logo_url: string | null }[];
  url_billetterie: string | null;
  url: string;
};

/* ————————————————— Les trois murs ————————————————— */

export type Vignette = {
  personne_id: string;
  prenom: string | null;
  nom: string | null;
  entreprise: string | null;
  secteur: string | null;
  photo_url: string | null;
  pseudo: string | null;
  premium: boolean;
};

export type Deal = {
  id: string;
  conclu_le: string;
  objet: string | null;
  a: Vignette;
  b: Vignette;
  recommande_par: Vignette | null;
};

export type Recherche = Vignette & {
  participant_id: string;
  client_ideal: string | null;
  partenaire_ideal: string | null;
  pour_quoi_faire: string | null;
  recherche: string | null;
  inscrit_le: string;
};

export type RangMur = Vignette & { rang: number; influence: number; points_jour: number };

export type Murs = {
  evenement: { id: string; nom: string; slug: string; debut_le: string; fin_le: string; lieu_ville: string | null };
  deals: Deal[];
  recherches: Recherche[];
  classement: RangMur[];
  compteurs: { participants: number; rendez_vous: number; deals: number };
  maj_le: string;
};

/* ————————————————— Points d'action ————————————————— */

export type ActionInfluence =
  | 'partage_reseau' | 'filleul_rejoint' | 'rdv_planifie' | 'rdv_accepte'
  | 'rdv_concluant' | 'profil_complete' | 'recommandation_acceptee';

export type DescriptionAction = {
  cle: ActionInfluence;
  points: number;
  maxFois: number | null;
  libelle: string;
  explication: string;
  fois: number;
  fait: boolean;
};

export type MesActions = { total: number; plafond: number; actions: DescriptionAction[] };

export type Affiche = {
  evenement: { id: string; nom: string; slug: string; debut_le: string; lieu_nom: string | null; lieu_ville: string | null };
  code: string;
  url: string;
  qr: string | null;
  moi: { prenom: string | null; nom: string | null; entreprise: string | null } | null;
};
